import { ScheduleAnalyticsEvent } from "@/constants/analytics/Scheduling";
import { ConfirmationAlertTestID } from "@/constants/TestID";
import type { OnCompletionCallback } from "@/hooks/useGroupClassRegistration";
import { useGroupClassRegistration } from "@/hooks/useGroupClassRegistration";
import { useAnalytics } from "@/services/Analytics";
import ConfirmationAlert from "@/ui/composed/alerts/ConfirmationAlert";
import { FAIconName } from "@/ui/core";

interface UnregisterGroupConfirmationAlertProps {
  forWhom?: string;
  groupClassSeriesExternalId: string;
  isOpen: boolean;
  onCompletion?: OnCompletionCallback;
  onDismiss: () => void;
  onPress?: () => void;
  patientExternalId: string;
}

const UnregisterGroupConfirmationAlert = ({
  forWhom,
  groupClassSeriesExternalId,
  isOpen,
  onCompletion,
  onDismiss,
  onPress,
  patientExternalId,
}: UnregisterGroupConfirmationAlertProps) => {
  const { track } = useAnalytics();
  const patientName = forWhom ? ` ${forWhom}` : "";
  const text = `Unregister${patientName} from this group`;
  const alertBody = `Once you unregister${patientName} from this group, you may not be able to register again.`;
  const title = `Are you sure you want to unregister${patientName}?`;

  const { unregisterForGroupClass } = useGroupClassRegistration({
    groupClassExternalId: groupClassSeriesExternalId,
    patientExternalId,
  });

  const handleAlertConfirm = () => {
    const eventName = forWhom
      ? ScheduleAnalyticsEvent.GroupEventDetailsUnregisterPatientFromGroup
      : ScheduleAnalyticsEvent.GroupEventDetailsUnregisterFromGroup;
    track(eventName);
    onPress?.();
    unregisterForGroupClass(({ isRegistered, wasRegistered }) => {
      onCompletion?.({ isRegistered, wasRegistered });
    });
  };

  return (
    <ConfirmationAlert
      confirmationType="destructive"
      confirmLeftIcon={FAIconName.UserSlash}
      confirmText={text}
      isOpen={isOpen}
      message={alertBody}
      onConfirm={handleAlertConfirm}
      onDismiss={onDismiss}
      testID={ConfirmationAlertTestID.UnregisterGroup}
      title={title}
    />
  );
};

export default UnregisterGroupConfirmationAlert;
